import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FileSaverService } from 'ngx-filesaver';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { MESSAGE_TYPE } from '../constants/message-type';
import { OperationResult } from '../interfaces/operation-result';
import { SharedStateService } from './shared-state.service';

@Injectable({
	providedIn: 'root'
})
export class FileService {
	#httpClient = inject(HttpClient);
	#fileSaver = inject(FileSaverService);
	#sharedStateService = inject(SharedStateService);

	downloadFile(url: string, data: any): Observable<OperationResult> {
		const DEFAULT_ERROR = 'Failed to download file. Either there is no data or there was a problem.';

		if (!this.#sharedStateService.IsLoggedIn()) {
			return of({ message: DEFAULT_ERROR, messageType: MESSAGE_TYPE.Warning } as OperationResult);
		}

		const HEADERS = new HttpHeaders({
			'Content-Type': 'application/json',
			'Accept': 'application/octet-stream, application/json',
		});

		return this.#httpClient.post(url, data, {
			headers: HEADERS,
			responseType: 'blob',
			observe: 'response'
		}).pipe(
			map((response: HttpResponse<Blob>) => {
				const contentDisposition = response.headers.get('Content-Disposition');

				// Success Path: File exists
				if (contentDisposition && response.body) {
					const fileName = contentDisposition.replace('attachment; filename=', '').trim();
					this.#fileSaver.save(response.body, fileName);
					return { message: 'Successfully downloaded file', messageType: MESSAGE_TYPE.Success };
				}

				return { message: DEFAULT_ERROR, messageType: MESSAGE_TYPE.Warning };
			}),
			catchError(async (err: HttpErrorResponse) => {
				let errorMessage = DEFAULT_ERROR;
				if (err.error instanceof Blob) {
					try {
						const text = await err.error.text();
						const parsed = JSON.parse(text);
						errorMessage = parsed.Message || parsed.message || text;
					} catch {
						errorMessage = await err.error.text() || DEFAULT_ERROR;
					}
				}

				return {
					message: errorMessage,
					messageType: MESSAGE_TYPE.Error
				} as OperationResult;
			}),
			switchMap(result => result instanceof Promise ? from(result) : of(result))
		);
	}

	downloadZipFile(url: string, data: any): Observable<OperationResult> {
		const HEADERS = new HttpHeaders({
			'Content-Type': 'application/json',
			'Accept': 'application/zip'
		});

		return this.#httpClient.post(url, data, { headers: HEADERS, responseType: 'blob', observe: 'response' })
			.pipe(map((response: HttpResponse<Blob>) => {
				const HEADERS = response.headers;
				const CONTENT_DISPOSITION = HEADERS.get('Content-Disposition');
				const FILE_NAME = CONTENT_DISPOSITION?.replace('attachment; filename=', '');
				const MEDIA_TYPE = 'application/zip';
				const BLOB = new Blob([response.body as Blob], { type: MEDIA_TYPE });
				this.#fileSaver.save(BLOB, FILE_NAME);

				return { message: 'Successfully downloaded file', messageType: MESSAGE_TYPE.Success } as OperationResult;
			}));
	}
}
